import { useCallback, useEffect, useState } from "react";
import { Loader2, Power, RefreshCw, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatApiError } from "@/lib/api";
import {
  fetchSatRobotStatus,
  reenqueueSatRobot,
  setSatRobotEnabled,
  unstickSatRobotQueue,
} from "@/lib/platformApi";

/**
 * Control global del robot SAT — solo diseñador BNT (Centro de licencias).
 */
/**
 * @param {object} [props]
 * @param {boolean} [props.embedded] - Dentro de acordeón/hub: sin título duplicado ni borde exterior.
 */
export default function SatRobotControlPanel({ embedded = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [turnOffDialogOpen, setTurnOffDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSatRobotStatus();
      setStatus(data);
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo cargar el estado del robot SAT"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const operational = Boolean(status?.operational);
  const envLocked = Boolean(status?.env_disabled);
  const uiEnabled = Boolean(status?.enabled ?? false);

  const applyRobotEnabled = async (nextOn, reasonText = pauseReason) => {
    setSaving(true);
    try {
      const data = await setSatRobotEnabled({
        enabled: nextOn,
        reason: nextOn ? "" : (reasonText || "").trim(),
      });
      setStatus(data);
      toast.success(nextOn ? "Robot SAT encendido" : "Robot SAT apagado");
      if (!nextOn) {
        setPauseReason("");
        setTurnOffDialogOpen(false);
      }
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo actualizar el robot SAT"));
    } finally {
      setSaving(false);
    }
  };

  const requestTurnOff = () => {
    setTurnOffDialogOpen(true);
  };

  const handleToggle = (nextOn) => {
    if (nextOn) {
      if (envLocked) {
        toast.warning(
          "El candado VF_SAT_AUTO_DOWNLOADS_DISABLED sigue bloqueando descargas aunque encienda el flag.",
        );
      }
      applyRobotEnabled(true);
      return;
    }
    requestTurnOff();
  };

  const confirmTurnOff = () => {
    applyRobotEnabled(false);
  };

  const runUnstick = async () => {
    setMaintenance(true);
    try {
      const data = await unstickSatRobotQueue();
      setStatus(data?.status || status);
      const u = data?.unstick || {};
      toast.success(
        `Cola liberada: ${u.spinning_queue_cleared ?? 0} fila(s), ${u.stale_processing_recovered ?? 0} atascado(s)`,
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo liberar la cola"));
    } finally {
      setMaintenance(false);
    }
  };

  const runReenqueue = async () => {
    setMaintenance(true);
    try {
      const data = await reenqueueSatRobot();
      setStatus(data?.status || status);
      const n = data?.reenqueue?.enqueued ?? 0;
      if (data?.reenqueue?.reason === "robot_not_operational") {
        toast.warning("El robot no está operacional; enciérdalo primero");
      } else {
        toast.success(`Re-encolados ${n} cliente(s)`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo re-encolar clientes"));
    } finally {
      setMaintenance(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando control del robot…
      </div>
    );
  }

  const q = status?.queue || {};
  const switchDisabled = saving || (envLocked && !uiEnabled);

  return (
    <div
      className={
        embedded
          ? "space-y-4 pt-3"
          : "rounded-md border border-amber-200 bg-amber-50/60 p-4 space-y-4"
      }
      data-testid="sat-robot-control-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {embedded ? null : (
            <h2 className="text-sm font-semibold text-[#0F2D5C] flex items-center gap-2">
              <Power className="h-4 w-4" />
              Control del robot SAT (scheduler)
            </h2>
          )}
          <p className={`text-xs text-slate-600 max-w-2xl ${embedded ? "" : "mt-1"}`}>
            Enciende o apaga las descargas automáticas en toda la plataforma sin redeploy en Railway.
            El candado <code className="text-[11px]">VF_SAT_AUTO_DOWNLOADS_DISABLED</code> en el servidor
            sigue teniendo prioridad.
          </p>
        </div>
        <span
          className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${
            operational
              ? "bg-emerald-100 text-emerald-800"
              : envLocked
                ? "bg-red-100 text-red-800"
                : "bg-slate-200 text-slate-700"
          }`}
          data-testid="sat-robot-status-badge"
        >
          {status?.status_label || "—"}
        </span>
      </div>

      {envLocked && uiEnabled ? (
        <p
          className="text-xs text-amber-900 bg-amber-100 border border-amber-300 rounded-md p-2 m-0"
          data-testid="sat-robot-env-locked-hint"
        >
          Candado Railway activo: las descargas están bloqueadas, pero el flag del diseñador sigue
          «encendido» en la base de datos. Use <strong>Apagar robot</strong> o el interruptor para
          dejarlo apagado al quitar el candado.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <Switch
            id="sat-robot-enabled"
            checked={uiEnabled}
            disabled={switchDisabled}
            onCheckedChange={handleToggle}
            data-testid="sat-robot-toggle"
          />
          <Label htmlFor="sat-robot-enabled" className="text-sm cursor-pointer">
            {envLocked && !uiEnabled
              ? "Bloqueado por variable de entorno"
              : uiEnabled
                ? "Robot encendido"
                : "Robot apagado"}
          </Label>
        </div>
        {uiEnabled ? (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={saving}
            onClick={requestTurnOff}
            data-testid="sat-robot-turn-off-btn"
          >
            Apagar robot
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading || saving}
          data-testid="sat-robot-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      <AlertDialog open={turnOffDialogOpen} onOpenChange={setTurnOffDialogOpen}>
        <AlertDialogContent data-testid="sat-robot-turn-off-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar robot SAT</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-700">
                <p>
                  Las descargas automáticas dejarán de encolarse en toda la plataforma. Puede volver a
                  encender el robot cuando lo necesite.
                </p>
                <div className="space-y-2 text-left">
                  <Label htmlFor="sat-robot-pause-reason-dialog" className="text-xs font-medium">
                    Motivo (opcional)
                  </Label>
                  <Input
                    id="sat-robot-pause-reason-dialog"
                    type="text"
                    className="text-sm bg-white"
                    placeholder="Ej. Fin primer ciclo BVL, piloto en pausa…"
                    value={pauseReason}
                    onChange={(e) => setPauseReason(e.target.value)}
                    maxLength={500}
                    data-testid="sat-robot-pause-reason"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !saving) {
                        e.preventDefault();
                        confirmTurnOff();
                      }
                    }}
                  />
                  <p className="text-[11px] text-slate-500 m-0">
                    Si lo deja vacío se guardará «Apagado desde panel diseñador».
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={saving}
              onClick={confirmTurnOff}
              data-testid="sat-robot-turn-off-confirm"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Apagando…
                </>
              ) : (
                "Apagar robot"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <dt className="text-slate-500">Sincronización activa</dt>
          <dd className="font-semibold text-[#0F2D5C]">{q.clients_sync_active ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Cola pendiente</dt>
          <dd className="font-semibold text-[#0F2D5C]">{q.queue_pending ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Procesando</dt>
          <dd className="font-semibold text-[#0F2D5C]">{q.queue_processing ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Último RFC worker</dt>
          <dd className="font-semibold text-[#0F2D5C] truncate">
            {status?.scheduler_checkpoint?.last_rfc || "—"}
          </dd>
        </div>
      </dl>

      <div className="flex flex-wrap gap-2 pt-1 border-t border-amber-200/80">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={maintenance}
          onClick={runUnstick}
          data-testid="sat-robot-unstick"
        >
          <Wrench className="h-3.5 w-3.5 mr-1" />
          Liberar cola atascada
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={maintenance || !operational}
          onClick={runReenqueue}
          data-testid="sat-robot-reenqueue"
        >
          Re-encolar debidos
        </Button>
      </div>
    </div>
  );
}
